import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, Bank, ArrowRight } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { formatINR } from "@/lib/format";
import SmartLoanCalculator from "@/components/SmartLoanCalculator";

const EMPTY = { name: "", phone: "", email: "", profession: "", designation: "", company_name: "", property_finalised: "", property_cost: "", loan_amount: "", loan_type: "" };
const LOAN_TYPES = ["Home Loan", "Loan Against Property", "Mortgage Loan", "Balance Transfer", "Top-up Loan", "Other"];

export default function HomeLoan() {
  const [sp] = useSearchParams();
  const refInfo = useMemo(() => {
    const propertyId = sp.get("property_id") || "";
    const projectId = sp.get("project_id") || "";
    const propertyName = sp.get("property_name") || "";
    const cost = Number(sp.get("property_cost")) || "";
    const loanType = sp.get("loan_type") || "";
    return { propertyId, projectId, propertyName, cost, loanType };
  }, [sp]);
  const [form, setForm] = useState(() => ({
    ...EMPTY,
    property_finalised: (refInfo.propertyId || refInfo.projectId) ? "yes" : "",
    property_cost: refInfo.cost || "",
    loan_type: refInfo.loanType,
  }));
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Please enter your full name"); return; }
    if (form.phone.replace(/\D/g, "").length < 10) { toast.error("Please enter a valid 10-digit mobile number"); return; }
    if (!form.loan_type) { toast.error("Please select a loan type"); return; }
    setBusy(true);
    try {
      await api.post("/leads", {
        name: form.name.trim(),
        phone: form.phone.trim(),
        loan_type: form.loan_type,
        email: form.email.trim() || undefined,
        profession: form.profession.trim() || undefined,
        designation: form.designation.trim() || undefined,
        company_name: form.company_name.trim() || undefined,
        property_finalised: form.property_finalised === "yes" ? true : form.property_finalised === "no" ? false : undefined,
        property_cost: Number(form.property_cost) || undefined,
        loan_amount: Number(form.loan_amount) || undefined,
        property_id: refInfo.propertyId || undefined,
        project_id: refInfo.projectId || undefined,
        message: `${form.loan_type} application${refInfo.propertyName ? ` for ${refInfo.propertyName}` : ""} — Profession: ${form.profession || "—"}, Designation: ${form.designation || "—"}, Company: ${form.company_name || "—"}`,
        source: "home_loan",
        landing_page: "/home-loan",
        source_url: window.location.href,
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="section-blue py-14">
        <div className="max-w-4xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 mb-4"><Bank size={14} weight="bold" /> Home Loan Assistance</div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight">Apply for a Home Loan</h1>
          <p className="text-slate-600 mt-3 max-w-2xl">Share your details and our loan desk will call you back with the best pre-approved offers across leading banks.</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-14 space-y-10">
        <div data-testid="loans-calculator">
          <div className="mb-5">
            <div className="text-xs uppercase tracking-widest text-blue-600 font-semibold mb-1">Financial Tools</div>
            <h2 className="text-2xl font-bold text-slate-900">Smart Loan Calculator</h2>
          </div>
          <SmartLoanCalculator />
        </div>
        <div>
        {done ? (
          <div data-testid="hl-thankyou" className="card-premium p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-5">
              <CheckCircle size={34} weight="fill" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Thank You!</h2>
            <p className="text-slate-600 leading-relaxed max-w-md mx-auto">
              Your home loan enquiry has been received. Our loan specialist will contact you within one business day with personalised offers.
            </p>
            <div className="flex gap-3 justify-center mt-8 flex-wrap">
              <Link to="/properties" className="btn-primary text-sm">Browse Properties <ArrowRight size={14} /></Link>
              <Link to="/emi-calculator" className="btn-secondary text-sm">EMI Calculator</Link>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="home-loan-form" className="card-premium p-8 space-y-4">
            {refInfo.propertyName && (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3" data-testid="hl-linked-listing">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-blue-600 font-semibold">{refInfo.projectId ? "Linked Project" : "Linked Property"}</div>
                  <div className="text-sm font-semibold text-slate-900">{refInfo.propertyName}</div>
                </div>
                {refInfo.cost ? <div className="text-sm font-bold text-blue-700">{formatINR(refInfo.cost)}</div> : null}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Fl label="Full Name *"><Input required data-testid="hl-name" value={form.name} onChange={e => set("name", e.target.value)} placeholder="Rajesh Kumar" className="h-11 rounded-lg border-slate-200" /></Fl>
              <Fl label="Mobile *"><Input required data-testid="hl-mobile" type="tel" value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="88288 30707" className="h-11 rounded-lg border-slate-200" /></Fl>
              <Fl label="Email"><Input data-testid="hl-email" type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="you@example.com" className="h-11 rounded-lg border-slate-200" /></Fl>
              <Fl label="Profession"><Input data-testid="hl-profession" value={form.profession} onChange={e => set("profession", e.target.value)} placeholder="Salaried / Self-employed" className="h-11 rounded-lg border-slate-200" /></Fl>
              <Fl label="Designation"><Input data-testid="hl-designation" value={form.designation} onChange={e => set("designation", e.target.value)} placeholder="Senior Manager" className="h-11 rounded-lg border-slate-200" /></Fl>
              <Fl label="Company Name"><Input data-testid="hl-company" value={form.company_name} onChange={e => set("company_name", e.target.value)} placeholder="Acme Ltd." className="h-11 rounded-lg border-slate-200" /></Fl>
              <Fl label="Property Finalised?">
                <Select value={form.property_finalised} onValueChange={v => set("property_finalised", v)}>
                  <SelectTrigger data-testid="hl-finalised" className="h-11 rounded-lg border-slate-200"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </Fl>
              <Fl label="Property Cost (₹)"><Input data-testid="hl-property-cost" type="number" min="0" value={form.property_cost} onChange={e => set("property_cost", e.target.value)} placeholder="8500000" className="h-11 rounded-lg border-slate-200" /></Fl>
              <Fl label="Loan Type / Loan Category *">
                <Select value={form.loan_type} onValueChange={v => set("loan_type", v)}>
                  <SelectTrigger data-testid="hl-loan-type" className="h-11 rounded-lg border-slate-200"><SelectValue placeholder="Select loan type" /></SelectTrigger>
                  <SelectContent>
                    {LOAN_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Fl>
              <Fl label="Loan Amount (₹)"><Input data-testid="hl-loan-amount" type="number" min="0" value={form.loan_amount} onChange={e => set("loan_amount", e.target.value)} placeholder="6000000" className="h-11 rounded-lg border-slate-200" /></Fl>
            </div>
            <button type="submit" disabled={busy} data-testid="hl-submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3.5 rounded-lg font-semibold shadow-md shadow-blue-500/20 disabled:opacity-60 transition-colors">
              {busy ? "Submitting…" : "Submit Application"}
            </button>
            <p className="text-xs text-slate-400 text-center">By submitting, you agree to be contacted by our loan desk.</p>
          </form>
        )}
        </div>
      </div>
    </div>
  );
}

function Fl({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 mb-1 block">{label}</label>
      {children}
    </div>
  );
}
